import React, { useEffect, useState } from "react";
import {
  AvailableDays,
  Reservation,
} from "../../../services/spaceReservationServices";
import HorizontalRule from "../../Rule/Horizontal";
import {
  RESERVATION_SHOWER_ROOM_TODAY_GAP_MINUTE,
  RESERVATION_SPACE_TYPES,
  reservationSpaceType,
} from "../../../types/reservation";

type Props = {
  startTime?: string; // '08:00'
  endTime?: string; // '18:00'
  reservationDate: Date;
  reservationTimeStart: string;
  reservationTimeEnd: string;
  availableTimeStart: string;
  availableTimeEnd: string;
  availableDays: AvailableDays;
  usePayment?: boolean;
  reservationData: Reservation[];
  onClickTimeSlot?: () => void;
  onSelectTimeRange?: (
    startTime: string,
    endTime: string,
    error: string
  ) => void;
  useBlockTime: boolean;
  blockTimeStart?: string;
  blockTimeEnd?: string;
  reservationSpaceType: reservationSpaceType;
  useAutoConfirm?: boolean;
};

export function getStartEndTimeString(startTime: string, endTime: string) {
  const startTimePrefix =
    timeToMinutes(startTime) < timeToMinutes("12:00") ? "오전 " : "오후 ";
  let endTimePrefix =
    timeToMinutes(endTime) < timeToMinutes("12:00") ? "오전 " : "오후 ";

  if (startTimePrefix === endTimePrefix)
    return startTimePrefix + startTime + " ~ " + endTime;
  else return startTimePrefix + startTime + " ~ " + endTimePrefix + endTime;
}

export function timeToMinutes(time: string) {
  const [hours, mins] = time.split(":").map(Number);
  return hours * 60 + mins;
}

// time1 >= time2 : true
export function timeCompare(
  time1: string,
  time2: string,
  equal: boolean = false
) {
  return equal
    ? timeToMinutes(time1) - timeToMinutes(time2) >= 0
    : timeToMinutes(time1) - timeToMinutes(time2) > 0;
}

export function getTimeCount(startTime: string, endTime: string) {
  return Math.floor(timeToMinutes(endTime) - timeToMinutes(startTime)) / 30;
}

export function getRangeMinutes(startTime: string, endTime: string) {
  return 30 * getTimeCount(startTime, endTime);
}

export function addMinutes(time: string, minutes: number) {
  const [hours, mins] = time.split(":").map(Number);
  const date = new Date();
  date.setHours(hours, mins);
  date.setMinutes(date.getMinutes() + minutes);

  const newHours = String(date.getHours()).padStart(2, "0");
  const newMinutes = String(date.getMinutes()).padStart(2, "0");

  return `${newHours}:${newMinutes}`;
}

export default function TimeSlots({
  startTime = "08:00",
  endTime = "17:30",
  reservationData,
  reservationDate,
  availableDays,
  reservationTimeStart,
  reservationTimeEnd,
  availableTimeStart,
  availableTimeEnd,
  usePayment = false,
  onClickTimeSlot,
  onSelectTimeRange,
  useBlockTime,
  blockTimeStart,
  blockTimeEnd,
  reservationSpaceType,
  useAutoConfirm,
}: Props) {
  const slots = getTimeCount(startTime, endTime) - 1;

  const [startSlot, setStartSlot] = useState<string>(reservationTimeStart);
  const [endSlot, setEndSlot] = useState<string>(reservationTimeEnd);

  const onSelect = (
    start: string,
    end: string,
    error = "예약이 불가능한 시간입니다"
  ) => {
    if (onSelectTimeRange) {
      onSelectTimeRange(start, end, error);
    }
  };
  const getNextSlotTime = (slotTime: string) => {
    return slotTime === "23:30" ? "24:00" : addMinutes(slotTime, 30);
  };
  const handleSlotClick = (slotTime: string) => {
    // console.log("handleSlotClick", slotTime, startSlot, endSlot);
    let nextSlotTime = getNextSlotTime(slotTime);

    if (startSlot && endSlot) {
      //다른영역 선택했을때
      // console.log("startSlot && endSlot", startSlot, endSlot);
      if (timeCompare(startSlot, slotTime)) {
        // 시간 반대로 선택 되었을때
        nextSlotTime = getNextSlotTime(startSlot);

        if (isReservedSlotIncluded(slotTime, startSlot)) {
          onSelect(slotTime, nextSlotTime, "연결된 시간으로만 예약 가능합니다");
        } else if (getIsUnavailable(slotTime)) {
          onSelect(slotTime, nextSlotTime);
        } else {
          setEndSlot(startSlot);
          setStartSlot(slotTime);
          onSelect(slotTime, nextSlotTime, "");
        }
      } else if (getIsHighlighted(slotTime)) {
        // 칠해진것 선택했을때
        if (slotTime === startSlot) {
          if (endSlot === nextSlotTime) {
            setStartSlot("");
            setEndSlot("");
            onSelect("", "", "");
          } else {
            setStartSlot(slotTime);
            setEndSlot(nextSlotTime);
            onSelect(slotTime, nextSlotTime, "");
          }
        } else {
          setEndSlot(nextSlotTime);
          onSelect(startSlot, nextSlotTime, "");
        }
      } else if (isReservedSlotIncluded(startSlot, slotTime)) {
        onSelect(startSlot, slotTime, "연결된 시간으로만 예약 가능합니다");
      } else if (getIsUnavailable(slotTime)) {
        onSelect(startSlot, slotTime);
      } else {
        setEndSlot(nextSlotTime);
        onSelect(startSlot, nextSlotTime, "");
      }
    } else if (!startSlot && !endSlot) {
      //둘다 비었을때, 최초선택시
      if (getIsUnavailable(slotTime)) {
        onSelect(startSlot, slotTime);
      } else {
        setStartSlot(slotTime);
        onSelect(slotTime, nextSlotTime, "");
      }
    } else {
      if (
        isBetweenSelectedSlots(
          slotTime,
          availableTimeStart,
          availableTimeEnd
        ) &&
        !isAlreadyReservedSlots(slotTime)
      ) {
        setStartSlot(slotTime);
        onSelect(slotTime, nextSlotTime, "");
      }
    }
  };

  const isBetweenSelectedSlots = (
    slotTime: string,
    start: string,
    end: string,
    startEqual: boolean = true,
    endEqual: boolean = true
  ) => {
    if (start && end) {
      return (
        (startEqual
          ? timeToMinutes(slotTime) >= timeToMinutes(start)
          : timeToMinutes(slotTime) > timeToMinutes(start)) &&
        (endEqual
          ? timeToMinutes(slotTime) <= timeToMinutes(end)
          : timeToMinutes(slotTime) < timeToMinutes(end))
      );
    }
    return false;
  };

  const isAlreadyReservedSlots = (slotTime: string) => {
    if (reservationData.length > 0) {
      return reservationData.some((reservation) =>
        usePayment || reservation.reservationStatus === "CONNECTION"
          ? isBetweenSelectedSlots(
              slotTime,
              reservation.beforeCleanTime,
              reservation.afterCleanTime, // TODO check - reservationTimeEnd(기존) > afterCleanTime 이 맞는지
              true,
              false
            ) ||
            (useBlockTime &&
              isBetweenSelectedSlots(
                slotTime,
                blockTimeStart as string,
                blockTimeEnd as string,
                true,
                false
              ))
          : isBetweenSelectedSlots(
              slotTime,
              reservation.reservationTimeStart,
              reservation.reservationTimeEnd,
              true,
              false
            ) ||
            (useBlockTime &&
              isBetweenSelectedSlots(
                slotTime,
                blockTimeStart as string,
                blockTimeEnd as string,
                true,
                false
              ))
      );
    } else if (reservationData.length === 0 && useBlockTime) {
      return isBetweenSelectedSlots(
        slotTime,
        blockTimeStart as string,
        blockTimeEnd as string,
        true,
        false
      );
    }
    return false;
  };

  const isReservedSlotIncluded = (start: string, end: string) => {
    if (start && end) {
      if (reservationData.length > 0) {
        return reservationData.some(
          (reservation) =>
            isBetweenSelectedSlots(
              reservation.reservationTimeStart,
              start,
              end,
              false,
              false
            ) ||
            isBetweenSelectedSlots(
              reservation.reservationTimeEnd,
              start,
              end,
              false,
              false
            ) ||
            (useBlockTime &&
              isBetweenSelectedSlots(
                blockTimeStart as string,
                start,
                end,
                false,
                false
              )) ||
            (useBlockTime &&
              isBetweenSelectedSlots(
                blockTimeEnd as string,
                start,
                end,
                false,
                false
              ))
        );
      } else if (reservationData.length === 0 && useBlockTime) {
        return (
          isBetweenSelectedSlots(
            blockTimeStart as string,
            start,
            end,
            false,
            false
          ) ||
          isBetweenSelectedSlots(
            blockTimeEnd as string,
            start,
            end,
            false,
            false
          )
        );
      }
    }
    return false;
  };

  const Slot = ({
    slotTime,
    isUnavailable,
    isHighlighted,
  }: {
    slotTime: string;
    isUnavailable: boolean;
    isHighlighted: boolean;
  }) => {
    let slotClassName =
      "w-full flex justify-center items-center p-2 border border-brand-neutral text-center bg-white cursor-pointer h-bottom-button h-[60px] rounded-md font-semibold text-sm leading-4";

    if (isUnavailable) {
      slotClassName +=
        " bg-brand-neutral-05 cursor-not-allowed text-brand-primary-gray-100 text-opacity-30 border-none disabled";
    } else if (isHighlighted) {
      slotClassName +=
        " border-brand-primary-magenta text-brand-primary-magenta";
    } else {
      slotClassName += " text-brand-neutral-60";
    }

    const slotTimePrint = (slotTime: string) => {
      return timeToMinutes(slotTime) > timeToMinutes("12:30")
        ? addMinutes(slotTime, -720)
        : slotTime;
    };

    return (
      <div
        className={slotClassName}
        onClick={() => handleSlotClick(slotTime)}
        data-testid={`${slotTime}-time-slot`}
      >
        {slotTimePrint(slotTime)} <br />~<br />
        {slotTimePrint(addMinutes(slotTime, 30))}
      </div>
    );
  };

  const isBeforeCurrentTime = (slotTime: string) => {
    const currentTime = new Date();
    let currentSlotTime = `${String(currentTime.getHours()).padStart(
      2,
      "0"
    )}:${String(currentTime.getMinutes()).padStart(2, "0")}`;

    if (
      reservationSpaceType === RESERVATION_SPACE_TYPES.SHOWER &&
      !useAutoConfirm
    ) {
      currentSlotTime = addMinutes(
        currentSlotTime,
        RESERVATION_SHOWER_ROOM_TODAY_GAP_MINUTE
      );
    }

    if (reservationDate.getTime() > currentTime.getTime()) {
      return false;
    }

    return timeCompare(currentSlotTime, slotTime, true);
  };

  const isValidDaysOfWeek = () => {
    const daysOfWeek: (keyof AvailableDays)[] = [
      "sunday",
      "monday",
      "tuesday",
      "wednesday",
      "thursday",
      "friday",
      "saturday",
    ];

    const day = reservationDate.getDay();
    if (availableDays[daysOfWeek[day]]) {
      return true;
    }
  };

  const getIsUnavailable = (slotTime: string) => {
    return (
      !isBetweenSelectedSlots(slotTime, availableTimeStart, availableTimeEnd) ||
      isBeforeCurrentTime(slotTime) ||
      isAlreadyReservedSlots(slotTime) ||
      !isValidDaysOfWeek()
    );
  };

  const getIsHighlighted = (slotTime: string) => {
    return (
      isBetweenSelectedSlots(slotTime, startSlot, endSlot, true, false) ||
      (startSlot === slotTime && !endSlot && startSlot !== "")
    );
  };

  const renderSlots = (slotsArray: string[]) =>
    slotsArray.map((slotTime, index) => {
      const isUnavailable = getIsUnavailable(slotTime);
      const isHighlighted = getIsHighlighted(slotTime);
      return (
        <Slot
          key={index}
          slotTime={slotTime}
          isUnavailable={isUnavailable}
          isHighlighted={isHighlighted}
        />
      );
    });

  const morningSlots = [];
  const afternoonSlots = [];

  for (let index = 0; index <= slots; index++) {
    const slotTime = addMinutes(availableTimeStart, index * 30);
    const isBefore = timeToMinutes(slotTime) < timeToMinutes("12:00");
    if (isBefore) {
      morningSlots.push(slotTime);
    } else {
      afternoonSlots.push(slotTime);
    }
  }

  const handleClickTimeSlot = () => {
    if (onClickTimeSlot) {
      onClickTimeSlot();
    }
  };

  useEffect(() => {
    setStartSlot(reservationTimeStart);
    setEndSlot(reservationTimeEnd);
  }, [reservationTimeStart, reservationTimeEnd]);

  return (
    <>
      <HorizontalRule margin="0" className="bg-black" thickness={2} />
      <div className="p-5 bg-[#F7F7F7]" onClick={handleClickTimeSlot}>
        <div className="w-full p-2">
          <div className="text-sm font-semibold leading-[19px] pb-[3px]">
            오전
          </div>
          <div className="grid grid-cols-4 gap-[3px]">
            {renderSlots(morningSlots)}
          </div>
        </div>
        <div className="w-full p-2 py-5">
          <div className="text-sm font-semibold leading-[19px] pb-[3px]">
            오후
          </div>
          <div className="grid grid-cols-4 gap-[3px]">
            {renderSlots(afternoonSlots)}
          </div>
        </div>
        <div className="w-full p-2 gap-[14px] flex flex-row items-center justify-end text-xs font-base leading-[18px]">
          <div className="flex items-center gap-1">
            <svg
              width="14"
              height="14"
              viewBox="0 0 14 14"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <circle cx="7" cy="7" r="7" fill="white" />
              <circle
                cx="7"
                cy="7"
                r="6"
                stroke="#333F48"
                strokeOpacity="0.2"
                strokeWidth="2"
              />
            </svg>
            가능
          </div>
          <div className="flex items-center gap-1">
            <svg
              width="14"
              height="14"
              viewBox="0 0 14 14"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <circle
                cx="7"
                cy="7"
                r="7"
                fill="url(#paint0_linear_3810_49565)"
              />
              <defs>
                <linearGradient
                  id="paint0_linear_3810_49565"
                  x1="14"
                  y1="0"
                  x2="-0.905265"
                  y2="3.83622"
                  gradientUnits="userSpaceOnUse"
                >
                  <stop stopColor="#EA27C2" />
                  <stop offset="1" stopColor="#E100A3" />
                </linearGradient>
              </defs>
            </svg>
            선택
          </div>
          <div className="flex items-center gap-1">
            <svg
              width="14"
              height="14"
              viewBox="0 0 14 14"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <circle cx="7" cy="7" r="6.5" fill="#E7E7E8" stroke="#C3C5C8" />
            </svg>
            불가
          </div>
        </div>
      </div>
    </>
  );
}
